import React, { useCallback, useEffect, useState } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  getSeoItem,
  createSeo,
  updateSeo,
  handleUpdateFields,
  handleClearFields,
  updateSeoAttributes,
} from "store/cms/pages/seo/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalSeoTabs } from "configs/types";

import Button from "components/buttons/Button";
import PagesSeoMainTab from "../seo/tabs/PagesSeoMainTab";
import styles from "../seo/CmsPagesSeoEditModal.module.scss";
import { Modal } from "components/Modal/Modal";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import PagesSeoSeoTab from "../seo/tabs/PagesSeoSeoTab";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import {
  getChangedSeoAttributes,
  normalizeSeoHtml,
} from "helpers/seoAttributes";

const CmsPagesSeoEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector(
    (store) => store["cmsSeo"].isItemFetched,
  );
  const payload = useAppSelector((store) => store["cmsSeo"].payload);
  const isItemFetching = useAppSelector(
    (store) => store["cmsSeo"].isItemFetching,
  );

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);

  const [tabs] = useState<ModalSeoTabs[]>([
    ModalSeoTabs.main,
    ModalSeoTabs.seo,
  ]);
  const [activeTab, setActiveTab] = useState<ModalSeoTabs>(ModalSeoTabs.main);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [initialSeoData, setInitialSeoData] = useState<any>();

  const permissions = usePermissionHelper();
  const canSave = isEdit
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getSeoItem(entityId));
    }
  }, [entityId, isItemFetched]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [activeTab]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canSave) {
      return;
    }

    if (payload && activeTab === ModalSeoTabs.main) {
      if (!isEdit) {
        formErrors.resetErrors();
      }

      try {
        if (isEdit) {
          await dispatch(
            updateSeo({ ...payload, id: Number(entityId) }),
          ).unwrap();
        } else {
          const newId = await dispatch(createSeo(payload)).unwrap();

          navigate(`${location.pathname}?entityId=${newId}`);
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t(isEdit ? "entityUpdateSuccess" : "entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
      return;
    }
    if (payload && activeTab === ModalSeoTabs.seo) {
      try {
        const { geo, locale } = payload;
        const attributesData = getChangedSeoAttributes(
          initialSeoData,
          payload,
          [
            "headTitle",
            "metaDescription",
            "openGraphTitle",
            "openGraphDescription",
            "twitterTitle",
            "twitterDescription",
            "fullDescription",
            "additionalDescription",
          ],
          {
            fullDescription: normalizeSeoHtml,
            additionalDescription: normalizeSeoHtml,
          },
        );

        if (!geo || !locale) {
          return;
        }

        const finalData = {
          geo,
          locale,
          ...attributesData,
        };

        await dispatch(
          updateSeoAttributes({
            id: Number(entityId),
            data: finalData,
          }),
        ).unwrap();
        setInitialSeoData(payload);

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }
  };

  const handleTabClick = (tab: ModalSeoTabs) => {
    if (!isEdit && tab !== ModalSeoTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
    setIsTabLoading(tab === ModalSeoTabs.seo);
  };

  const handleTabLoadingChange = useCallback((isLoading: boolean) => {
    setIsTabLoading(isLoading);
  }, []);

  const renderTab = () => {
    if (activeTab === ModalSeoTabs.main) {
      return (
        <PagesSeoMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!canSave}
        />
      );
    }
    if (activeTab === ModalSeoTabs.seo) {
      return (
        <PagesSeoSeoTab
          id={entityId || ""}
          handleData={(data) => dispatch(handleUpdateFields(data))}
          currentData={payload}
          onInitialData={setInitialSeoData}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!canSave}
          onLoadingChange={handleTabLoadingChange}
        />
      );
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("Seo")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content
            className={cn(
              styles.cmsPagesSeoContentWrapper,
              activeTab === ModalSeoTabs.seo && styles.seoContentLocked,
            )}
          >
            <div className="seo-content">{renderTab()}</div>
          </Modal.Content>

          <Modal.Footer>
            {canSave &&
              (isTabLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    isSecondary
                    handleClick={handleApply}
                    txt={t("save")}
                  />
                </>
              ))}
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default CmsPagesSeoEditModal;
